import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { AxiosResponse } from 'axios';
import { apiServiceRTQ } from './apiShow';

type UseGetApiListOptions = {
  params?: Record<string, any>;
  enabled?: boolean;
  retry?: number;
};

export const useGetApiList = <T = any>(
  url: string,
  options: UseGetApiListOptions = {},
): UseQueryResult<AxiosResponse<T>> => {
  return useQuery<AxiosResponse<T>>({
    queryKey: ['getApi', url, options.params],
    queryFn: ({}) => apiServiceRTQ.get<T>(url, { params: options.params }),
    enabled: options.enabled ?? true,
    retry: options.retry ?? 1,
  });
};

type UseGetApiInfiniteListOptions = UseGetApiListOptions & {
  pageSize?: number;
};

export const useGetApiInfiniteList = (
  url: string,
  options: UseGetApiInfiniteListOptions = {},
) => {
  const pageSize = options.pageSize ?? 20;

  const query = useInfiniteQuery<any>({
    queryKey: ['getApi', url, options.params, 'infinite', pageSize],
    queryFn: ({ pageParam }) =>
      apiServiceRTQ.get(url, {
        params: { ...options.params, pageNumber: pageParam, pageSize },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages: any[]) =>
      lastPage?.meta_data?.has_next ? allPages.length + 1 : undefined,
    enabled: options.enabled ?? true,
    retry: options.retry ?? 1,
  });

  const pages = query.data?.pages;
  const data = useMemo(
    () =>
      pages?.length
        ? {
            ...pages[pages.length - 1],
            data: pages.flatMap((page: any) => page?.data ?? []),
          }
        : undefined,
    [pages],
  );

  return {
    ...query,
    data,
    isListLoading: query.isFetching && !query.isFetchingNextPage,
  };
};

type GetApiParams = { url: string };
// Replace this with the actual expected response type
type GetApiResponse = any;

export const useMutateGetApi = () => {
  const queryClient = useQueryClient();

  return useMutation<GetApiResponse, unknown, GetApiParams>({
    mutationFn: ({ url }) => apiServiceRTQ.get(url),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['getApi', variables.url], data);
    },
  });
};
